import React from 'react';
import { useI18n } from '../i18n';
import { WaitlistForm } from './WaitlistForm';
import { Shield, Sparkles, Lock, ArrowUp, Github } from 'lucide-react';
import { trackNavClick, trackOutboundClick } from '../lib/analytics';

export const Footer: React.FC = () => {
  const { t } = useI18n();

  const scrollToTop = () => {
    trackNavClick('#top', 'Footer Scroll To Top');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-slate-100 dark:bg-navy-950 pt-20 pb-12 border-t border-slate-300 dark:border-navy-800 transition-colors">
      {/* Secondary Bottom CTA Banner */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="glow-card rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden border-gold-500/30 shadow-gold-glow-lg">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-gold-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-xs font-semibold text-gold-600 dark:text-gold-400 mb-4 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            <span>{t.cta.badge}</span>
          </div>

          <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-4 max-w-2xl mx-auto">
            {t.cta.title}
          </h2>

          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 max-w-xl mx-auto mb-8">
            {t.cta.description}
          </p>

          <WaitlistForm source="footer_cta" className="max-w-md" />

          <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
            <a
              href="https://github.com/workouse"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick('github', 'https://github.com/workouse')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-navy-850 border border-slate-300 dark:border-navy-700 text-xs font-semibold text-slate-800 dark:text-slate-200 hover:text-gold-600 dark:hover:text-gold-400 transition-colors shadow-sm"
            >
              <Github className="w-4 h-4" />
              <span>{t.cta.githubButton}</span>
            </a>
          </div>

          <p className="mt-4 text-[11px] text-slate-500">{t.cta.disclaimer}</p>
        </div>
      </div>

      {/* Main Footer Links & Copyright */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-8 border-b border-slate-200 dark:border-navy-850">
          {/* Logo & Tagline */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white dark:bg-navy-850 border border-gold-500/40 flex items-center justify-center shadow-sm">
              <Shield className="w-4 h-4 text-gold-500" />
            </div>
            <div className="flex flex-col">
              <span className="text-base font-bold text-slate-900 dark:text-white tracking-tight">
                Workouse <span className="text-gold-500">AI</span>
              </span>
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {t.footer.dataSovereignty} • {t.footer.openSourceNote}
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap items-center gap-6 text-xs text-slate-600 dark:text-slate-400 font-medium">
            <a
              href="#features"
              onClick={() => trackNavClick('#features', t.footer.links.architecture)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors"
            >
              {t.footer.links.architecture}
            </a>
            <a
              href="https://github.com/workouse"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick('github', 'https://github.com/workouse')}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors flex items-center gap-1"
            >
              <Github className="w-3.5 h-3.5" />
              <span>{t.footer.links.github}</span>
            </a>
            <a
              href="#dlp"
              onClick={() => trackNavClick('#dlp', t.footer.links.docs)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors"
            >
              {t.footer.links.docs}
            </a>
            <a
              href="#"
              onClick={() => trackNavClick('#', t.footer.links.privacy)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors"
            >
              {t.footer.links.privacy}
            </a>
            <a
              href="#"
              onClick={() => trackNavClick('#', t.footer.links.terms)}
              className="hover:text-gold-600 dark:hover:text-gold-400 transition-colors"
            >
              {t.footer.links.terms}
            </a>
          </div>

          {/* Back to Top */}
          <button
            type="button"
            onClick={scrollToTop}
            className="p-2.5 rounded-lg bg-white dark:bg-navy-850 border border-slate-300 dark:border-navy-700 hover:border-gold-500/50 text-slate-600 dark:text-slate-400 hover:text-gold-600 dark:hover:text-gold-400 transition-colors shadow-sm"
            aria-label="Back to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>

        {/* Bottom Legal Notice */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-slate-500">
          <p>© {new Date().getFullYear()} Workouse AI Inc. {t.footer.rights}</p>
          <div className="flex items-center gap-2">
            <Lock className="w-3 h-3 text-gold-500" />
            <span>Open Source • In-Memory DLP Vault • Zero Data Leakage</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
